import { URITypeMap } from '../../../enums/uri_type';
import { URIParticle } from '../../../enums/uri_particle';
import * as factory from '../../../factories';
import * as internalFactory from '../../../_internal/factories';
import { decodeComponent, encodeComponent } from '../../helpers';
import { createParserDefinition } from '../helpers';
import { UserURIParsers } from './user';
const [declareURIParser, URIParsers] = createParserDefinition();
declareURIParser(URITypeMap.ALBUM, (context) => {
    const base = context.getBase62IdComponent();
    const disc = context.getNextComponent();
    return factory.albumURI(base, disc ? parseInt(disc, 10) : undefined);
});
declareURIParser(URIParticle.APP, (context, format) => {
    const id = context.getNextRequiredComponent();
    const decodedId = decodeComponent(id, format);
    if (encodeComponent(decodedId, format) !== id) {
        throw new TypeError('Invalid or unknown Spotify URI!');
    }
    const args = context.getRemainingComponents();
    for (let i = 0, len = args.length; i < len; ++i)
        args[i] = decodeComponent(args[i], format);
    return factory.applicationURI(id, args);
});
declareURIParser(URITypeMap.AD, (context) => {
    return factory.adURI(context.getNextRequiredComponent());
});
declareURIParser(URITypeMap.ARTIST, (context) => {
    const id = context.getBase62IdComponent();
    const nextComponent = context.getNextComponent();
    if (nextComponent === URIParticle.TOP) {
        return factory.artistToplistURI(id, context.getNextRequiredComponent());
    }
    if (nextComponent === URIParticle.CONCERTS) {
        return factory.artistConcertsURI(id);
    }
    return factory.artistURI(id);
});
declareURIParser(URITypeMap.AUDIO, (context) => {
    return factory.audioURI(context.getIdComponent());
});
declareURIParser(URITypeMap.AUDIO_FILE, (context) => {
    return factory.audioFileURI(context.getNextRequiredComponent(), context.getNextRequiredComponent());
});
declareURIParser(URITypeMap.DAILY_MIX, (context) => {
    return factory.dailyMixURI(context.getIdComponent());
});
declareURIParser(URITypeMap.TEMP_PLAYLIST, (context) => {
    return factory.temporaryPlaylistURI(context.getNextRequiredComponent(), context.getRemainingString());
});
declareURIParser(URITypeMap.PLAYLIST, (context) => {
    return factory.playlistV2URI(context.getBase62IdComponent());
});
declareURIParser(URITypeMap.SEARCH, (context, format) => {
    return factory.searchURI(decodeComponent(context.getRemainingString(), format));
});
declareURIParser(URITypeMap.TRACK, (context) => {
    const query = context.getQueryParams();
    return factory.trackURI(context.getBase62IdComponent(), context.getNextComponent(), query === null || query === void 0 ? void 0 : query.context, query === null || query === void 0 ? void 0 : query.play);
});
declareURIParser(URITypeMap.TRACKSET, (context) => {
    var _a;
    const name = decodeComponent(context.getNextComponent());
    let tracksArray = context.getNextRequiredComponent();
    const hashSign = context.getNextComponent();
    let index = parseInt((_a = context.getNextComponent()) !== null && _a !== void 0 ? _a : '', 10);
    // Sanity check: %23 is URL code for "#"
    if (hashSign !== '%23' || isNaN(index)) {
        index = null;
    }
    const tracksetTracks = [];
    if (tracksArray) {
        tracksArray = decodeComponent(tracksArray).split(',');
        for (let i = 0, len = tracksArray.length; i < len; i++) {
            const trackId = tracksArray[i];
            if (trackId) {
                tracksetTracks.push(factory.trackURI(trackId));
            }
        }
    }
    return factory.tracksetURI(tracksetTracks, name, index);
});
declareURIParser(URITypeMap.CONTEXT_GROUP, (context) => {
    return factory.contextGroupURI(context.getNextRequiredComponent(), context.getNextRequiredComponent());
});
declareURIParser(URIParticle.TOP, (context) => {
    const type = context.getNextRequiredComponent();
    if (context.getNextComponent() === URIParticle.GLOBAL) {
        return factory.toplistURI(type, undefined, true);
    }
    return factory.toplistURI(type, context.getNextComponent(), false);
});
declareURIParser(URITypeMap.LOCAL_TRACK, (context, format) => {
    const artistNameComponent = context.getNextComponent();
    const artistName = decodeComponent(artistNameComponent, format);
    const albumNameComponent = context.getNextComponent();
    const albumName = decodeComponent(albumNameComponent, format);
    const trackNameComponent = context.getNextComponent();
    const trackName = decodeComponent(trackNameComponent, format);
    const durationComponent = context.getNextComponent();
    const duration = durationComponent
        ? parseInt(durationComponent, 10)
        : undefined;
    if (trackNameComponent !== undefined) {
        return factory.localTrackURI(artistName, albumName, trackName, duration);
    }
    else if (albumNameComponent !== undefined) {
        return factory.localAlbumURI(artistName, albumName);
    }
    return factory.localArtistURI(artistName);
});
declareURIParser(URITypeMap.IMAGE, (context) => {
    return factory.imageURI(context.getHexIdComponent(40));
});
declareURIParser(URITypeMap.MOSAIC, (context) => {
    return factory.mosaicURI(context.getRemainingComponents());
});
declareURIParser(URITypeMap.RADIO, (context) => {
    return factory.radioURI(context.getRemainingString());
});
declareURIParser(URITypeMap.S4A_THIRD_PARTY, (context) => {
    const id = context.getBase62IdComponent();
    return factory.s4aThirdPartyURI(id);
});
declareURIParser(URITypeMap.SPECIAL, (context, format) => {
    const args = context.getRemainingComponents();
    for (let i = 0, len = args.length; i < len; ++i)
        args[i] = decodeComponent(args[i], format);
    return factory.specialURI(args);
});
declareURIParser(URITypeMap.STATION, (context) => {
    return factory.stationURI(context.getRemainingComponents());
});
declareURIParser(URITypeMap.EPISODE, (context) => {
    const query = context.getQueryParams();
    return factory.episodeURI(context.getBase62IdComponent(), query === null || query === void 0 ? void 0 : query.context, query === null || query === void 0 ? void 0 : query.play);
});
declareURIParser(URITypeMap.SHOW, (context) => {
    return factory.showURI(context.getBase62IdComponent());
});
declareURIParser(URITypeMap.LIVE, (context) => {
    return factory.liveURI(context.getIdComponent());
});
declareURIParser(URITypeMap.ROOM, (context) => {
    return factory.roomURI(context.getIdComponent());
});
declareURIParser(URITypeMap.EXPRESSION, (context) => {
    return factory.expressionURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CONCEPT, (context) => {
    return factory.conceptURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CONCERT, (context) => {
    return factory.concertURI(context.getIdComponent());
});
declareURIParser(URITypeMap.MERCH, (context) => {
    return factory.merchURI(context.getIdComponent());
});
declareURIParser(URITypeMap.MERCHHUB, () => {
    return factory.merchHubURI();
});
declareURIParser(URITypeMap.PRERELEASE, (context) => {
    return factory.prereleaseURI(context.getIdComponent());
});
declareURIParser(URITypeMap.SOCIAL_SESSION, (context) => {
    return factory.socialSessionURI(context.getNextRequiredComponent());
});
declareURIParser(URITypeMap.JAM, (context) => {
    const [_, contextType, id, play] = context.getAllComponents();
    if (contextType === URITypeMap.ALBUM ||
        contextType === URITypeMap.ARTIST ||
        contextType === URITypeMap.PLAYLIST) {
        return factory.jamURI(id, contextType, !!play);
    }
    return factory.jamURI('');
});
declareURIParser(URITypeMap.INTERRUPTION, (context) => {
    return factory.interruptionURI(context.getNextRequiredComponent());
});
declareURIParser(URITypeMap.COLLECTION, (context) => {
    return factory.collectionURI(undefined, context.getNextComponent());
});
declareURIParser(URITypeMap.GENRE, (context) => {
    return factory.genreURI(context.getNextRequiredComponent());
});
declareURIParser(URITypeMap.QUEUE, () => {
    return factory.queueURI();
});
declareURIParser(URIParticle.USER, (context, format) => {
    const username = decodeComponent(context.getNextRequiredComponent(), format);
    const text = context.getNextComponent();
    if (username === URITypeMap.FACEBOOK && text) {
        return factory.facebookURI(text);
    }
    else if (text && UserURIParsers[text]) {
        const uriParser = UserURIParsers[text];
        if (!uriParser) {
            throw new TypeError('Invalid Spotify URI!');
        }
        return uriParser(context, format, { username });
    }
    const remainingComponents = context.getRemainingComponents();
    const args = text ? [text].concat(remainingComponents) : undefined;
    return factory.profileURI(username, args);
});
declareURIParser(URITypeMap.UNKNOWN, (context) => {
    const args = context.getAllComponents();
    return internalFactory.unknownURI(args);
});
declareURIParser(URITypeMap.MEDIA, (context) => {
    return factory.mediaURI(context.getIdComponent());
});
declareURIParser(URITypeMap.POLL, (context) => {
    return factory.pollURI(context.getIdComponent());
});
declareURIParser(URITypeMap.QUESTION, (context) => {
    return factory.questionURI(context.getIdComponent());
});
declareURIParser(URITypeMap.RESPONSE, (context) => {
    return factory.responseURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CONTRIBUTOR, (context) => {
    return factory.contributorURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CULTURAL_MOMENT, (context) => {
    const particle = context.getNextComponent();
    if (particle !== URIParticle.HUB) {
        throw new TypeError('Invalid or unknown Spotify URI!');
    }
    return factory.culturalMomentURI(context.getIdComponent());
});
declareURIParser(URITypeMap.FESTIVAL, (context) => {
    return factory.festivalURI(context.getIdComponent());
});
declareURIParser(URITypeMap.TOUR, (context) => {
    return factory.tourURI(context.getIdComponent());
});
declareURIParser(URITypeMap.VENUE, (context) => {
    return factory.venueURI(context.getIdComponent());
});
declareURIParser(URITypeMap.VIDEO, (context) => {
    return factory.videoURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CACHED_FILES, () => {
    return factory.cachedFilesURI();
});
declareURIParser(URITypeMap.CANVAS, (context) => {
    return factory.canvasURI(context.getIdComponent());
});
declareURIParser(URITypeMap.CHAT_MESSAGE, (context) => {
    return factory.chatMessageURI(context.getIdComponent());
});
declareURIParser(URITypeMap.COURSE, (context) => {
    return factory.courseURI(context.getIdComponent());
});
declareURIParser(URITypeMap.LESSON, (context) => {
    return factory.lessonURI(context.getIdComponent());
});
declareURIParser(URITypeMap.PROMOTER, (context) => {
    return factory.promoterURI(context.getIdComponent());
});
declareURIParser(URITypeMap.COMMENT, (context) => {
    return factory.commentURI(context.getIdComponent());
});
declareURIParser(URITypeMap.PROMOTION, (context) => {
    return factory.promotionURI(context.getIdComponent());
});
declareURIParser(URITypeMap.SITE, (context) => {
    return factory.siteURI(context.getIdComponent());
});
declareURIParser(URITypeMap.SUPPLEMENTARY_MATERIAL, (context) => {
    return factory.supplementaryMaterialURI(context.getIdComponent());
});
declareURIParser(URITypeMap.PRESENTS, (context) => {
    return factory.presentsURI(context.getIdComponent());
});
export { URIParsers };
//# sourceMappingURL=index.js.map